import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { Plus } from 'lucide-react'
import { toast } from 'sonner'
import { useResources } from '../hooks/useResources'
import {
  Badge,
  Button,
  ConfirmDialog,
  DataTable,
  DropdownMenu,
  PageHeader,
  PageLayout,
  PageToolbar,
  SearchField,
  RecordIdentity,
  RecordWorkspace,
  StatusBadge,
} from '../components/common'
import { ResourceForm } from '../components/ResourceForm'
import styles from './ResourcesPage.module.css'

const RESOURCE_TYPE_LABELS = {
  postgresql: 'PostgreSQL',
  redis: 'Redis',
  rest_api: 'REST API',
  graphql: 'GraphQL',
  gcs: 'Google Cloud Storage',
}

function formatResourceType(type) {
  return RESOURCE_TYPE_LABELS[type] || String(type || '').replaceAll('_', ' ')
}

export function ResourcesPage() {
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState('')
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [deleteResource, setDeleteResource] = useState(null)

  const { useResourcesList, useCreateResource, useDeleteResource, useTestResource } = useResources()
  const { data: resources = [], isLoading, isError, error, refetch } = useResourcesList()
  const createMutation = useCreateResource()
  const deleteMutation = useDeleteResource()
  const testMutation = useTestResource()

  const filteredResources = useMemo(() => {
    const list = Array.isArray(resources) ? resources : []
    const query = searchTerm.trim().toLocaleLowerCase()
    if (!query) return list
    return list.filter((resource) => (
      resource.name.toLocaleLowerCase().includes(query)
      || resource.type.toLocaleLowerCase().includes(query)
    ))
  }, [resources, searchTerm])

  const handleCreateResource = async (resourceData) => {
    try {
      await createMutation.mutateAsync(resourceData)
      setShowCreateDialog(false)
      toast.success('Resource created')
    } catch (mutationError) {
      toast.error(`Failed to create resource: ${mutationError.response?.data?.error || mutationError.message}`)
      throw mutationError
    }
  }

  const handleTestResource = (resource) => {
    testMutation.mutate(resource.id, {
      onSuccess: () => {
        toast.success(`${resource.name} connection passed`)
        refetch()
      },
      onError: (mutationError) => {
        toast.error(`Test failed for ${resource.name}: ${mutationError.response?.data?.error || mutationError.message}`)
      },
    })
  }

  const handleDeleteResource = () => {
    if (!deleteResource) return
    deleteMutation.mutate(deleteResource.id, {
      onSuccess: () => {
        setDeleteResource(null)
        toast.success('Resource deleted')
        refetch()
      },
      onError: (mutationError) => {
        toast.error(`Failed to delete resource: ${mutationError.response?.data?.error || mutationError.message}`)
      },
    })
  }

  const columns = [
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      priority: 'primary',
      sticky: 'start',
      className: styles.resourceColumn,
      render: (row) => (
        <RecordIdentity
          title={row.name}
          subtitle={`${formatResourceType(row.type)} resource`}
          titleAttribute={row.name}
        />
      ),
    },
    {
      key: 'type',
      label: 'Type',
      sortable: true,
      priority: 'secondary',
      render: (row) => <span className={styles.typeLabel}>{formatResourceType(row.type)}</span>,
    },
    {
      key: 'health_status',
      label: 'Health',
      priority: 'primary',
      render: (row) => (
        <StatusBadge auto>
          {row.health_status || 'unknown'}
        </StatusBadge>
      ),
    },
    {
      key: 'last_health_check',
      label: 'Last check',
      priority: 'tertiary',
      render: (row) => (
        <span className={styles.lastCheck}>
          {row.last_health_check ? new Date(row.last_health_check).toLocaleString() : 'Never'}
        </span>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      priority: 'primary',
      sticky: 'end',
      className: styles.actionsColumn,
      render: (row) => (
        <div className={styles.actions} onClick={(event) => event.stopPropagation()}>
          <DropdownMenu
            triggerLabel={`Resource actions for ${row.name}`}
            items={[
              {
                key: 'test',
                label: 'Test connection',
                onSelect: () => handleTestResource(row),
                disabled: testMutation.isPending || testMutation.isLoading,
              },
              {
                key: 'delete',
                label: 'Delete resource',
                onSelect: () => setDeleteResource(row),
                danger: true,
              },
            ]}
          />
        </div>
      ),
    },
  ]

  const toolbar = (
    <PageToolbar
      label="Resource controls"
      search={(
        <SearchField
          value={searchTerm}
          onChange={setSearchTerm}
          label="Search resources"
          placeholder="Search resources…"
        />
      )}
      actions={<Badge compact>{filteredResources.length} {filteredResources.length === 1 ? 'resource' : 'resources'}</Badge>}
    />
  )

  return (
    <PageLayout size="wide">
      <PageHeader
        title="Resources"
        primaryAction={(
          <Button onClick={() => setShowCreateDialog(true)}>
            <Plus size={16} aria-hidden="true" />
            Add resource
          </Button>
        )}
      />

      <RecordWorkspace
        label="Resource records"
        toolbar={toolbar}
        loading={isLoading}
        error={isError ? (error || new Error('Resources could not load')) : null}
        items={filteredResources}
        filtered={Boolean(searchTerm.trim())}
        emptyTitle="No resources yet"
        emptyDescription="Add a connection before using it in forms or workflows."
        searchEmptyTitle="No resources match your search"
        onRetry={refetch}
        onClear={() => setSearchTerm('')}
      >
        <DataTable
          data={filteredResources}
          columns={columns}
          loading={isLoading}
          loadingMessage="Loading resources…"
          density="compact"
          label="Resources"
          responsiveColumns
          onRowClick={(resource) => navigate(`/dashboard/resources/${resource.id}`)}
        />
      </RecordWorkspace>

      <ResourceForm
        open={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        onSubmit={handleCreateResource}
        title="Create resource"
      />

      <ConfirmDialog
        open={Boolean(deleteResource)}
        title="Delete resource"
        message={`Delete “${deleteResource?.name}”? This action cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
        onConfirm={handleDeleteResource}
        onCancel={() => setDeleteResource(null)}
      />
    </PageLayout>
  )
}
